'use client';
 
import * as React from 'react';
import { type HTMLMotionProps, motion, type Transition } from 'motion/react';
import { cva, type VariantProps } from 'class-variance-authority';
 
import { cn } from '@/lib/utils';
 
const buttonVariants = cva(
  "relative overflow-hidden cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 group",
  {
    variants: {
      variant: {
        default: 'bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] text-white hover:from-[#8a2be2] hover:to-[#c780ff] shadow-lg hover:shadow-xl hover:shadow-[#8a2be2]/50',
        destructive:
          'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 dark:from-red-700 dark:to-red-800 shadow-lg hover:shadow-xl hover:shadow-red-500/50',
        outline:
          'border-2 border-[#6a0dad] bg-transparent text-[#6a0dad] hover:bg-[#6a0dad] hover:text-white dark:border-[#8a2be2] dark:text-[#8a2be2] dark:hover:bg-[#8a2be2] dark:hover:text-white hover:shadow-lg',
        secondary:
          'bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 shadow-md hover:shadow-lg',
        ghost:
          'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md gap-1.5 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);
 
const rippleVariants = cva('absolute rounded-full size-5 pointer-events-none', {
  variants: {
    variant: {
      default: 'bg-white/30',
      destructive: 'bg-white/30',
      outline: 'bg-gray-400/30',
      secondary: 'bg-gray-500/30',
      ghost: 'bg-gray-400/30',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});
 
type Ripple = {
  id: number;
  x: number;
  y: number;
};
 
type RippleButtonProps = HTMLMotionProps<'button'> & {
  children: React.ReactNode;
  rippleClassName?: string;
  scale?: number;
  transition?: Transition;
} & VariantProps<typeof buttonVariants>;
 
function RippleButton({
  children,
  onClick,
  className,
  rippleClassName,
  variant,
  size,
  scale = 10,
  transition = { duration: 0.6, ease: 'easeOut' },
  ...props
}: RippleButtonProps) {
  const [ripples, setRipples] = React.useState<Ripple[]>([]);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
 
  const createRipple = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const button = buttonRef.current;
      if (!button) return;
 
      const rect = button.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
 
      const newRipple: Ripple = {
        id: Date.now(),
        x,
        y,
      };
 
      setRipples((prev) => [...prev, newRipple]);
 
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
      }, 600);
    },
    [],
  );
 
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      createRipple(event);
      if (onClick) {
        onClick(event);
      }
    },
    [createRipple, onClick],
  );
 
  return (
    <motion.button
      ref={buttonRef}
      onClick={handleClick}
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.05, y: -2 }}
      className={cn(buttonVariants({ variant, size, className }))}
      {...(props as any)}
    >
      <span className="relative z-10 flex items-center gap-2">
        {children}
      </span>
      {/* Shine effect on hover */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        initial={{ x: '-100%', opacity: 0 }}
        whileHover={{ x: '100%', opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
      />
      {/* Glow effect */}
      {variant === 'default' && (
        <motion.div
          className="absolute -inset-1 rounded-lg opacity-0 group-hover:opacity-100 blur-md -z-10"
          style={{
            background: 'radial-gradient(circle, rgba(106,13,173,0.5) 0%, transparent 70%)'
          }}
          transition={{ duration: 0.3 }}
        />
      )}
      {ripples.map((ripple) => (
        <motion.span
          key={ripple.id}
          initial={{ scale: 0, opacity: 0.5 }}
          animate={{ scale, opacity: 0 }}
          transition={transition}
          className={cn(
            rippleVariants({ variant, className: rippleClassName }),
          )}
          style={{
            top: ripple.y - 10,
            left: ripple.x - 10,
          }}
        />
      ))}
    </motion.button>
  );
}
 
export { RippleButton, type RippleButtonProps };

